import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ChevronRight, 
  GitPullRequest, 
  GitMerge, 
  XCircle, 
  MessageSquare, 
  Eye, 
  CheckCircle, 
  AlertCircle,
  User,
  Calendar,
  Code,
  ThumbsUp,
  ThumbsDown,
  MessageCircle,
  Edit,
  X,
  Send
} from 'lucide-react';
import { 
  getPullRequest, 
  addPRComment, 
  submitPRReview, 
  mergePullRequest, 
  closePullRequest,
  updatePullRequest,
  getPrompt 
} from '../services/api';
import { formatDistanceToNow } from 'date-fns';

const PullRequestDetail = ({ user }) => {
  const { promptId, prId } = useParams();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [pr, setPr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commenting, setCommenting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [merging, setMerging] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [newReview, setNewReview] = useState({
    type: 'comment',
    summary: ''
  });
  const [mergeMessage, setMergeMessage] = useState('');

  useEffect(() => {
    loadData();
  }, [promptId, prId]);

  const loadData = async () => {
    try {
      const [promptData, prData] = await Promise.all([
        getPrompt(promptId),
        getPullRequest(prId, user.id)
      ]);
      setPrompt(promptData);
      setPr(prData);
      setMergeMessage(`Merge PR: ${prData.title}`);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setCommenting(true);
      await addPRComment(prId, {
        content: newComment,
        userId: user.id
      });
      setNewComment('');
      loadData(); // Reload to show new comment
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setCommenting(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!newReview.summary.trim() && newReview.type !== 'comment') return;

    try {
      setReviewing(true);
      await submitPRReview(prId, {
        reviewType: newReview.type,
        summary: newReview.summary,
        userId: user.id
      });
      setNewReview({ type: 'comment', summary: '' });
      loadData(); // Reload to show new review
    } catch (error) {
      console.error('Failed to submit review:', error);
    } finally {
      setReviewing(false);
    }
  };

  const handleMergePR = async () => {
    if (!window.confirm('Are you sure you want to merge this pull request?')) {
      return;
    }

    try {
      setMerging(true);
      await mergePullRequest(prId, {
        mergeMessage,
        userId: user.id
      });
      navigate(`/prompts/${promptId}`);
    } catch (error) {
      console.error('Failed to merge PR:', error);
      setMerging(false);
    }
  };

  const handleClosePR = async () => {
    if (!window.confirm('Are you sure you want to close this pull request?')) {
      return;
    }

    try {
      await closePullRequest(prId, user.id);
      loadData();
    } catch (error) {
      console.error('Failed to close PR:', error);
    }
  };

  const renderDiff = (diff) => {
    if (!diff || !diff.lineDiff) return null;

    return (
      <div className="bg-gray-50 border rounded-lg overflow-hidden">
        <div className="bg-gray-100 px-4 py-2 border-b">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Changes</span>
            <span className="text-gray-600">
              +{diff.lineDiff.filter(part => part.added).length} 
              -{diff.lineDiff.filter(part => part.removed).length}
            </span>
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {diff.lineDiff.map((part, index) => {
            const lines = part.value.split('\n').filter(line => line !== '' || part.added || part.removed);
            
            return lines.map((line, lineIndex) => {
              if (line === '' && !part.added && !part.removed) return null;
              
              let className = 'px-4 py-1 font-mono text-sm';
              let prefix = ' ';
              
              if (part.added) {
                className += ' bg-green-100 text-green-800';
                prefix = '+';
              } else if (part.removed) {
                className += ' bg-red-100 text-red-800';
                prefix = '-';
              }
              
              return (
                <div key={`${index}-${lineIndex}`} className={className}>
                  <span className="select-none text-gray-400 mr-4">{prefix}</span>
                  {line}
                </div>
              );
            });
          })}
        </div>
      </div>
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <GitPullRequest className="h-5 w-5 text-green-600" />;
      case 'merged':
        return <GitMerge className="h-5 w-5 text-purple-600" />;
      case 'closed':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'draft':
        return <Edit className="h-5 w-5 text-gray-600" />;
      default:
        return <GitPullRequest className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 text-green-800';
      case 'merged':
        return 'bg-purple-100 text-purple-800';
      case 'closed':
        return 'bg-red-100 text-red-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getReviewIcon = (reviewType) => {
    switch (reviewType) {
      case 'approve':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'request_changes':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <MessageCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!pr || !prompt) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Pull request not found</h2>
        <Link to="/" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  const canMerge = pr.status === 'open' && (pr.target_branch_owner_id === user.id || pr.project_owner_id === user.id);
  const canClose = pr.status === 'open' && (pr.created_by === user.id || pr.project_owner_id === user.id);

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/" className="hover:text-primary-600">Dashboard</Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/projects/${prompt.project_id}`} className="hover:text-primary-600">
          {prompt.project_name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/prompts/${prompt.id}`} className="hover:text-primary-600">
          {prompt.name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/prompts/${promptId}/pull-requests`} className="hover:text-primary-600">
          Pull Requests
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-gray-900">#{pr.id.substring(0, 8)}</span>
      </nav>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          {getStatusIcon(pr.status)}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{pr.title}</h1>
            <div className="flex items-center space-x-4 mt-2">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(pr.status)}`}>
                {pr.status.charAt(0).toUpperCase() + pr.status.slice(1)}
              </span>
              <span className="text-gray-600">
                {pr.source_branch_name} → {pr.target_branch_name}
              </span>
              <span className="text-gray-500">
                <User className="h-4 w-4 inline mr-1" />
                {pr.created_by_name} opened {formatDistanceToNow(new Date(pr.created_at), { addSuffix: true })}
              </span>
            </div>
          </div>
        </div>

        {pr.status === 'open' && (
          <div className="flex space-x-3">
            {canMerge && (
              <button
                onClick={handleMergePR}
                disabled={merging}
                className="btn-primary"
              >
                <GitMerge className="h-4 w-4 mr-2" />
                {merging ? 'Merging...' : 'Merge'}
              </button>
            )}
            {canClose && (
              <button
                onClick={handleClosePR}
                className="btn-secondary text-red-600 hover:text-red-700"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Close
              </button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {pr.description && (
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Description</h3>
                <div className="prose max-w-none">
                  <pre className="whitespace-pre-wrap text-sm">{pr.description}</pre>
                </div>
              </div>
            </div>
          )}

          {/* Diff */}
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Changes</h3>
              {renderDiff(pr.diff)}
            </div>
          </div>

          {/* Comments and Reviews */}
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Discussion ({pr.comments.length + pr.reviews.length})
              </h3>
              
              <div className="space-y-4">
                {/* Combine comments and reviews, sort by date */}
                {[...pr.comments.map(c => ({...c, type: 'comment'})), 
                  ...pr.reviews.map(r => ({...r, type: 'review'}))]
                  .sort((a, b) => new Date(a.created_at || a.submitted_at) - new Date(b.created_at || b.submitted_at))
                  .map((item, index) => (
                    <div key={index} className="flex space-x-3 p-4 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0">
                        {item.type === 'review' ? getReviewIcon(item.review_type) : <MessageSquare className="h-4 w-4 text-gray-600" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 text-sm">
                          <span className="font-medium">{item.author_name || item.reviewer_name}</span>
                          <span className="text-gray-500">
                            {item.type === 'review' ? 'reviewed' : 'commented'}
                          </span>
                          <span className="text-gray-400">
                            {formatDistanceToNow(new Date(item.created_at || item.submitted_at), { addSuffix: true })}
                          </span>
                          {item.type === 'review' && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              item.review_type === 'approve' ? 'bg-green-100 text-green-800' :
                              item.review_type === 'request_changes' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {item.review_type.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 text-sm text-gray-700">
                          {item.content || item.summary}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* Add Comment */}
              {pr.status === 'open' && (
                <div className="mt-6 p-4 border rounded-lg">
                  <div className="flex space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-primary-600" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        className="input"
                        rows={3}
                        placeholder="Add a comment..."
                      />
                      <div className="flex justify-end mt-2">
                        <button
                          onClick={handleAddComment}
                          disabled={!newComment.trim() || commenting}
                          className="btn-primary text-sm"
                        >
                          <Send className="h-4 w-4 mr-1" />
                          {commenting ? 'Adding...' : 'Comment'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Review Section */}
          {pr.status === 'open' && (
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Submit Review</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Review Type
                    </label>
                    <div className="space-y-2">
                      {[
                        { value: 'comment', label: 'Comment', icon: MessageCircle },
                        { value: 'approve', label: 'Approve', icon: CheckCircle },
                        { value: 'request_changes', label: 'Request Changes', icon: AlertCircle }
                      ].map((option) => {
                        const Icon = option.icon;
                        return (
                          <label key={option.value} className="flex items-center">
                            <input
                              type="radio"
                              value={option.value}
                              checked={newReview.type === option.value}
                              onChange={(e) => setNewReview({ ...newReview, type: e.target.value })}
                              className="mr-2"
                            />
                            <Icon className="h-4 w-4 mr-2" />
                            {option.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Summary
                    </label>
                    <textarea
                      value={newReview.summary}
                      onChange={(e) => setNewReview({ ...newReview, summary: e.target.value })}
                      className="input"
                      rows={3}
                      placeholder="Leave a review summary..."
                    />
                  </div>
                  
                  <button
                    onClick={handleSubmitReview}
                    disabled={(!newReview.summary.trim() && newReview.type !== 'comment') || reviewing}
                    className="btn-primary w-full"
                  >
                    {reviewing ? 'Submitting...' : 'Submit Review'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Merge Section */}
          {canMerge && (
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Merge Pull Request</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Merge Message
                    </label>
                    <input
                      type="text"
                      value={mergeMessage}
                      onChange={(e) => setMergeMessage(e.target.value)}
                      className="input"
                      placeholder="Merge commit message"
                    />
                  </div>
                  
                  <button
                    onClick={handleMergePR}
                    disabled={merging}
                    className="btn-primary w-full"
                  >
                    <GitMerge className="h-4 w-4 mr-2" />
                    {merging ? 'Merging...' : 'Merge Pull Request'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Details */}
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Details</h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Created by:</span>
                  <span className="font-medium">{pr.created_by_name}</span>
                </div>
                
                {pr.assigned_to_name && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Assigned to:</span>
                    <span className="font-medium">{pr.assigned_to_name}</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Created:</span>
                  <span>{formatDistanceToNow(new Date(pr.created_at), { addSuffix: true })}</span>
                </div>
                
                {pr.merged_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Merged:</span>
                    <span>{formatDistanceToNow(new Date(pr.merged_at), { addSuffix: true })}</span>
                  </div>
                )}
                
                {pr.closed_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Closed:</span>
                    <span>{formatDistanceToNow(new Date(pr.closed_at), { addSuffix: true })}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PullRequestDetail; 